import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, HandHeart, Ruler, Hammer, Check, Sparkles, Clock, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Reveal, EstimateBanner } from '../components/shared';
import { PageHeader } from './Flooring';
import { steps, reasons, business } from '../mock/mock';

const stepIcons = [Truck, HandHeart, Ruler, Hammer];
const reasonIcons = [Sparkles, HandHeart, ShieldCheck, Clock];

export default function MobileShowroom() {
  return (
    <>
      <PageHeader
        eyebrow="How We Work"
        title="Our showroom rolls up to your driveway"
        sub="No fluorescent-lit warehouse. No pushy sales floor. Just hundreds of premium samples, seen in your home’s real light."
        image="https://images.unsplash.com/photo-1664794624325-73c18d92d5a3?auto=format&fit=crop&w=1920&q=80"
      />

      {/* intro split */}
      <section className="max-w-7xl mx-auto px-6 py-20 md:py-24 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <img src="https://images.unsplash.com/photo-1655457397686-4dd23e78a918?auto=format&fit=crop&w=1200&q=80" alt="Fanned wood flooring samples" className="rounded-2xl w-full h-[460px] object-cover shadow-lg" />
        </Reveal>
        <Reveal delay={120}>
          <span className="text-brand-clay text-xs font-semibold uppercase tracking-[0.25em]">The mobile difference</span>
          <h2 className="font-display text-3xl md:text-[40px] font-semibold text-brand-charcoal mt-3 leading-tight text-balance">Choosing floors should feel easy</h2>
          <p className="mt-5 text-brand-stone text-[17px] leading-relaxed">Flooring is one of the biggest visual decisions in your home — yet most people pick it under harsh store lighting, miles from the room it’s going in. We flipped that. Montifloors brings the entire experience to you.</p>
          <p className="mt-4 text-brand-stone text-[17px] leading-relaxed">Compare samples against your walls, your furniture, and your daylight. Ask every question. Take your time. There’s zero pressure and zero obligation.</p>
          <Button asChild className="mt-8 bg-brand-clay hover:bg-brand-clayDark text-white rounded-full px-7 h-13 py-3.5 font-medium">
            <Link to="/estimate">Book Your In-Home Visit</Link>
          </Button>
        </Reveal>
      </section>

      {/* steps */}
      <section className="bg-brand-cream">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-24">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-brand-clay text-xs font-semibold uppercase tracking-[0.25em]">Four simple steps</span>
              <h2 className="font-display text-3xl md:text-[42px] font-semibold text-brand-charcoal mt-3 leading-tight">From sample to installed floor</h2>
            </div>
          </Reveal>
          <div className="mt-16 grid gap-8 md:grid-cols-4">
            {steps.map((s, idx) => {
              const Icon = stepIcons[idx];
              return (
                <Reveal key={s.id} delay={idx * 90}>
                  <div className="bg-white rounded-2xl p-7 border border-brand-line shadow-sm h-full">
                    <div className="w-14 h-14 rounded-xl bg-brand-clay/10 text-brand-clay flex items-center justify-center"><Icon size={24} /></div>
                    <p className="mt-5 text-xs font-semibold text-brand-clay uppercase tracking-wider">Step {s.id}</p>
                    <h3 className="mt-1 font-display text-xl font-semibold text-brand-charcoal">{s.title}</h3>
                    <p className="mt-2 text-brand-stone text-[15px] leading-relaxed">{s.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* reasons */}
      <section className="max-w-7xl mx-auto px-6 py-20 md:py-24">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src="https://images.pexels.com/photos/8293641/pexels-photo-8293641.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Consultant explaining flooring options to homeowners" className="rounded-2xl w-full h-[480px] object-cover shadow-lg" />
          </Reveal>
          <Reveal delay={120}>
            <span className="text-brand-clay text-xs font-semibold uppercase tracking-[0.25em]">Why homeowners choose us</span>
            <h2 className="font-display text-3xl md:text-[40px] font-semibold text-brand-charcoal mt-3 leading-tight text-balance">Big-store selection, personal service</h2>
            <div className="mt-8 space-y-6">
              {reasons.map((r, idx) => {
                const Icon = reasonIcons[idx];
                return (
                  <div key={r.title} className="flex gap-4">
                    <span className="w-11 h-11 rounded-xl bg-brand-clay/10 text-brand-clay flex items-center justify-center shrink-0"><Icon size={20} /></span>
                    <div>
                      <h3 className="font-semibold text-brand-charcoal text-lg">{r.title}</h3>
                      <p className="text-brand-stone text-[15px] mt-1 leading-relaxed">{r.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      <EstimateBanner />
    </>
  );
}
